import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { createOrder, createGuestOrder } from "../Redux/Actions/OrderActions";
import { ORDER_CREATE_RESET } from "../Redux/Constants/OrderConstants";
import { DISCOUNT_VALIDATE_RESET } from "../Redux/Constants/DiscountConstants";
import Header from "../components/Header";
import Message from "../components/LoadingError/Error";
import ShippingComponent from "./ShippingComponent";
import DiscountInput from "../components/homeComponents/DiscountForm";
import "../App.css";
import "./payment.css";

const PlaceOrderScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cart         = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.userLogin);
  const guest        = useSelector((state) => state.guest);
  const isGuest      = guest?.isGuest && !userInfo;
  const displayName  = userInfo?.name  || "Guest";
  const displayEmail = userInfo?.email || guest?.email || "";

  const [selectedRate, setSelectedRate] = useState(null);
  const [discount, setDiscount]         = useState(null);

  const shippingRef = useRef(null);

  // ── Price helpers ─────────────────────────────────────────────────────────
  const getItemPrice = (item) => {
    const sale = parseFloat(item.salePrice);
    if (!isNaN(sale) && sale > 0) return sale;
    const reg = parseFloat(item.price);
    return !isNaN(reg) ? reg : 0;
  };

  const getItemQty = (item) => {
    const qty = parseInt(item.qty);
    return !isNaN(qty) && qty > 0 ? qty : 1;
  };

  const itemsTotal = cart.cartItems.reduce(
    (acc, item) => acc + getItemPrice(item) * getItemQty(item),
    0
  );

  const qualifiesForFreeShipping = itemsTotal >= 50;

  useEffect(() => {
    if (qualifiesForFreeShipping && shippingRef.current) {
      shippingRef.current.fetchRates();
    }
  }, [qualifiesForFreeShipping]);

  const calculatePrices = () => {
    const itemsPrice      = itemsTotal;
    const shippingPrice   = qualifiesForFreeShipping
      ? 0
      : selectedRate ? parseFloat(selectedRate.amount) || 0 : 0;
    const taxPrice        = Number(((7.75 / 100) * itemsPrice).toFixed(2));
    const discountSavings = discount ? discount.savings : 0;
    const totalPrice      = Number(
      (itemsPrice + shippingPrice + taxPrice - discountSavings).toFixed(2)
    );
    return { itemsPrice, shippingPrice, taxPrice, discountSavings, totalPrice };
  };

  const { itemsPrice, shippingPrice, taxPrice, discountSavings, totalPrice } =
    calculatePrices();

  // ── Order state ───────────────────────────────────────────────────────────
  const orderCreate = useSelector((state) => state.orderCreate);
  const { order, success, error } = orderCreate;

  const emailExists = error === "__EMAIL_EXISTS__";

  // ── Redirect on success ───────────────────────────────────────────────────
  useEffect(() => {
    if (success) {
      setDiscount(null);
      dispatch({ type: DISCOUNT_VALIDATE_RESET });
      dispatch({ type: ORDER_CREATE_RESET });
      navigate(`/order/${order._id}`);
    }
  }, [success]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clear stale email-exists error when user logs in and returns ──────────
  useEffect(() => {
    if (userInfo) {
      dispatch({ type: ORDER_CREATE_RESET });
    }
  }, [userInfo, dispatch]);

  // ── Place order ───────────────────────────────────────────────────────────
  const canPlaceOrder = () => {
    if (qualifiesForFreeShipping && itemsPrice > 0) return true;
    return selectedRate !== null;
  };

  const placeOrderHandler = () => {
    const orderItems = cart.cartItems.map((item) => ({
      ...item,
      price:      getItemPrice(item),
      qty:        getItemQty(item),
      dimensions: item.dimensions,
    }));

    const orderPayload = {
      orderItems,
      shippingAddress: cart.shippingAddress,
      paymentMethod:   cart.paymentMethod,
      itemsPrice,
      shippingPrice,
      taxPrice,
      totalPrice,
      rate:            selectedRate,
      discountCode:    discount?.code       || null,
      discountId:      discount?.discountId || null,
      discountAmount:  discount?.savings    || 0,
      addressTo: {
        name:    displayName,
        street1: cart.shippingAddress.street1 || "",
        street2: cart.shippingAddress.street2 || "",
        city:    cart.shippingAddress.city    || "",
        state:   cart.shippingAddress.state   || "",
        zip:     cart.shippingAddress.zipcode || "",
        country: cart.shippingAddress.country || "US",
        phone:   cart.shippingAddress.phone   || "",
        email:   displayEmail,
      },
    };

    if (isGuest) {
      dispatch(createGuestOrder({ ...orderPayload, guestEmail: displayEmail }));
    } else {
      dispatch(createOrder(orderPayload));
    }
  };

  return (
    <>
      <Header />
      <div className="container pb-mobile-bar">

        {/* ── Top info row ── */}
        <div className="row order-detail">

          {/* Customer */}
          <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
            <div className="row">
              <div className="col-md-4 center">
                <div className="alert-success order-box">
                  <i className="fas fa-user"></i>
                </div>
              </div>
              <div className="col-md-8 center">
                <h5><strong>Customer</strong></h5>
                <p>{displayName}</p>
                <p>{displayEmail}</p>
                {isGuest && (
                  <span style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>
                    Guest order
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Order Info */}
          <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
            <div className="row">
              <div className="col-md-4 center">
                <div className="alert-success order-box">
                  <i className="fas fa-truck-moving"></i>
                </div>
              </div>
              <div className="col-md-8 center">
                <h5><strong>Order info</strong></h5>
                <p>Shipping: {cart.shippingAddress.country}</p>
                <p>Payment method: {cart.paymentMethod}</p>
                {qualifiesForFreeShipping && (
                  <p className="free-shipping-badge text-success">
                    <i className="fas fa-check-circle"></i> Free Ground Shipping
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Delivery */}
          <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
            <div className="row">
              <div className="col-md-4 center">
                <div className="alert-success order-box">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
              </div>
              <div className="col-md-8 center">
                <h5><strong>Deliver to</strong></h5>
                <p>
                  Address: {cart.shippingAddress.city},{" "}
                  {cart.shippingAddress.state},{" "}
                  {cart.shippingAddress.street1},{" "}
                  {cart.shippingAddress.zipcode}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Products + summary ── */}
        <div className="row order-products justify-content-between">

          {/* Items list */}
          <div className="col-lg-8">
            {cart.cartItems.length === 0 ? (
              <Message variant="alert-info mt-5">Your cart is empty</Message>
            ) : (
              cart.cartItems.map((item, index) => {
                const price    = getItemPrice(item);
                const qty      = getItemQty(item);
                const subtotal = price * qty;
                return (
                  <div className="order-product row" key={index}>
                    <div className="col-md-3 col-6">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="col-md-5 col-6 d-flex align-items-center">
                      <Link to={`/products/${item.product}`}>
                        <h6>{item.name}</h6>
                      </Link>
                    </div>
                    <div className="mt-3 mt-md-0 col-md-2 col-6 d-flex align-items-center flex-column justify-content-center">
                      <h4>QUANTITY</h4>
                      <h6>{qty}</h6>
                    </div>
                    <div className="mt-3 mt-md-0 col-md-2 col-6 align-items-end d-flex flex-column justify-content-center">
                      <h4>SUBTOTAL</h4>
                      <h6>${subtotal.toFixed(2)}</h6>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Summary + shipping + place order */}
          <div className="col-lg-3 d-flex align-items-end flex-column mt-5 subtotal-order">

            {qualifiesForFreeShipping && itemsTotal > 0 && (
              <div className="free-shipping-banner mb-3 p-2 bg-light border border-success rounded text-center w-100">
                <span className="text-success fw-bold">
                  <i className="fas fa-truck me-2"></i> Free Ground Shipping Applied!
                </span>
              </div>
            )}

            {cart.cartItems.length > 0 && (
              <div className="w-100">
                <DiscountInput
                  itemsPrice={itemsPrice}
                  onDiscountApplied={setDiscount}
                />
              </div>
            )}

            <table className="table table-bordered">
              <tbody>
                <tr>
                  <td><strong>Products</strong></td>
                  <td>${itemsPrice.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Shipping</strong>
                    {qualifiesForFreeShipping && (
                      <span className="free-ship-label ms-2 badge bg-success">FREE</span>
                    )}
                  </td>
                  <td>${shippingPrice.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Tax</strong></td>
                  <td>${taxPrice.toFixed(2)}</td>
                </tr>
                {discount && (
                  <tr style={{ color: "#198754" }}>
                    <td>
                      <strong>Discount</strong>{" "}
                      <span style={{
                        fontSize:     11,
                        background:   "#d1fae5",
                        border:       "1px solid #6ee7b7",
                        borderRadius: 4,
                        padding:      "1px 6px",
                        fontFamily:   "monospace",
                        fontWeight:   700,
                      }}>
                        {discount.code}
                      </span>
                    </td>
                    <td>−${discountSavings.toFixed(2)}</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td><strong>Total</strong></td>
                  <td>${totalPrice.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {cart.cartItems.length > 0 && (
              <div className="shipping-section w-100">
                <ShippingComponent
                  ref={shippingRef}
                  cart={cart}
                  userInfo={userInfo}
                  selectedRate={selectedRate}
                  setSelectedRate={setSelectedRate}
                  qualifiesForFreeShipping={qualifiesForFreeShipping}
                />
                {selectedRate && !qualifiesForFreeShipping && (
                  <div className="alert alert-info py-2 mt-2 small">
                    Selected: {selectedRate.provider} ({selectedRate.estimated_days} days)
                  </div>
                )}

                {/* ── Error / email-exists banner ── */}
                {emailExists ? (
                  <div
                    className="alert alert-warning mt-3"
                    style={{ fontSize: 13, borderRadius: 8 }}
                  >
                    <strong>You already have an account</strong> with{" "}
                    <strong>{displayEmail}</strong>. Log in to place your order
                    and it will be saved to your account automatically.
                    <div className="mt-2">
                      <Link
                        to="/login?redirect=placeorder"
                        className="btn btn-sm btn-warning"
                      >
                        Log In to Continue
                      </Link>
                    </div>
                  </div>
                ) : (
                  error && <Message variant="alert-danger mt-2">{error}</Message>
                )}

                <button
                  type="submit"
                  onClick={placeOrderHandler}
                  className={`btn btn-success w-100 mt-3 d-none d-lg-block ${canPlaceOrder() ? "" : "disabled"}`}
                  disabled={!canPlaceOrder()}
                >
                  PLACE ORDER
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile sticky bar ── */}
      {cart.cartItems.length > 0 && (
        <div className="mobile-place-order-bar d-lg-none">
          <div className="mobile-place-order-inner">
            <span className="mobile-order-total">
              Total <strong>${totalPrice.toFixed(2)}</strong>
            </span>
            <button
              type="button"
              onClick={placeOrderHandler}
              className="btn btn-success mobile-order-btn"
              disabled={!canPlaceOrder()}
            >
              PLACE ORDER
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PlaceOrderScreen;